import { CircleCheck, CircleAlert, Clock, Info } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { SsoAlert } from '@/features/auth/components/SsoAlert'
import { SsoButtons } from '@/features/auth/components/SsoButtons'
import { ssoErrorKey, ssoErrorTone } from '@/features/auth/lib/ssoErrors'
import { inviteLandingPath, ssoInviteStash, type InviteLanding } from '@/features/auth/lib/ssoInviteStash'
import { useSsoErrorParam } from '@/features/auth/lib/useSsoErrorParam'
import {
  activeOrgSelected,
  probeSession,
  refreshMe,
  selectSessionStatus,
  selectSessionUser,
  signOut
} from '@/features/auth/slices/sessionSlice'
import { invitesHttp } from '@/features/invites/invites.http'
import type { ApiInviteValidation } from '@/features/invites/invites.http'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { cn } from '@/lib/utils'
import { isApiError } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store'

// 44px phone targets are CSS px on purpose: the root font is 14px, so a rem step
// (min-h-11 = 2.75rem) measures 38.5px (fix wave 1, VM1).
const inputClass =
  'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground max-sm:min-h-[44px]'
const primaryButtonClass =
  'h-9 w-full rounded-md bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 max-sm:min-h-[44px]'
// h-auto + min-h-9: the label can carry a long session address and must wrap (V2).
const secondaryButtonClass =
  'inline-flex h-auto min-h-9 w-full items-center justify-center rounded-md border border-input px-4 py-2 text-center text-sm text-ink-soft hover:bg-muted max-sm:min-h-[44px]'

/** The server's word for an acceptance by a session that belongs to another address. */
const EMAIL_MISMATCH = 'auth.invite.email-mismatch'

/** How long the welcome shows before the landing navigation. */
const LANDING_DELAY_MS = 800

/** A wire value that is really there: a non-empty string, never `null` (H1). */
const present = (value: string | null | undefined): value is string => typeof value === 'string' && value !== ''

/** Why a validate failed, from the server's key; `other` keeps the generic copy. */
type FailedKind = 'expired' | 'revoked' | 'accepted' | 'other'

const failedKindFor = (backendKey: string | undefined): FailedKind => {
  switch (backendKey) {
    case 'auth.invite.token-expired':
      return 'expired'
    case 'auth.invite.token-invalid':
      return 'revoked'
    case 'auth.invite.already-accepted':
      return 'accepted'
    default:
      return 'other'
  }
}

/**
 * The public accept's refusal for an address whose account has a password: it
 * must sign in and accept from there (TASK-714FBFBD fix cycle 1b, INV-S9).
 */
const ACCOUNT_EXISTS = 'auth.invite.account-exists'

/** The accept page's own copy for an SSO error key (spec R.5.4 + the design's two added rows). */
const inviteSsoMessage = (key: string): string => {
  switch (key) {
    case ACCOUNT_EXISTS:
      return 'invites:signInToAccept.body'
    case 'oauth.invite.email-mismatch':
      return 'invites:sso.errors.emailMismatch'
    case 'oauth.invite.email-unverified':
      return 'invites:sso.errors.emailUnverified'
    case 'oauth.invite.invalid':
      return 'invites:sso.errors.invalid'
    case 'oauth.provider.unconfigured':
      return 'invites:sso.errors.unavailable'
    case 'oauth.provider.denied':
      return 'invites:sso.errors.cancelled'
    case 'auth.account.deactivated':
      return 'errors:auth.account.deactivated'
    default:
      return 'invites:sso.errors.generic'
  }
}

/** An SSO message, and whether it came from a start that failed on this page (it then takes focus). */
interface SsoMessage {
  key: string
  fromStart: boolean
}

/**
 * `/accept-invite` (PX-W6.2 manual-pass gap) — the landing the invitation
 * email links to. The token rides the query string; the page validates it on
 * mount, shows the invited identity, and either accepts directly (an
 * existing user) or creates the account first (name + password). On success
 * the session is established and the visitor lands on `/`, where RoleLanding
 * renders the derived role's destination.
 *
 * Like `/login`, this surface sits OUTSIDE AppShell and the guards: an
 * invitee has no session yet — the token IS the credential.
 *
 * SSO (CR-SSO-INVITE-PX-2026-09-26): in the ready phase, for a new AND an
 * existing user, "Continue with Google / Microsoft" sits under the primary
 * action with a hint naming the invited address; it renders only when a
 * provider is configured. A verified provider email equal to the invite's
 * provisions (or joins) in one step on the server. A failed round trip returns
 * here as `?ssoError=<key>` and renders in one alert slot under the lede; the
 * invited address in the mismatch message comes from this page's own validate
 * response, never from the redirect. The route is live, so it carries no
 * ConceptBadge (Agon's ruling, 2026-09-26).
 */
export default function AcceptInvitePage() {
  const { t } = useTranslation(['invites', 'errors', 'peopleAdmin', 'common'])
  const toMessage = useApiErrorMessage()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  // Seeded once from the URL; the hook strips the param (keeping `token`).
  const initialSsoError = useSsoErrorParam()
  const [ssoMessage, setSsoMessage] = useState<SsoMessage | null>(() =>
    initialSsoError === null ? null : { key: initialSsoError, fromStart: false }
  )
  // An existing account accepts SIGNED IN (INV-S9): with a session, Accept goes
  // to the signed-in route; without one, the public route answers ACCOUNT_EXISTS.
  const sessionStatus = useAppSelector(selectSessionStatus)
  const signedIn = sessionStatus === 'authenticated'
  const sessionEmail = useAppSelector(selectSessionUser)?.email ?? ''
  // A sign-out also passes through `authenticating`; only the probe holds the page.
  const [signingOut, setSigningOut] = useState(false)
  const sessionSettling = sessionStatus === 'authenticating' && !signingOut

  const onSsoError = useCallback((key: string) => {
    setSsoMessage({ key: ssoErrorKey(key), fromStart: true })
  }, [])
  const onSsoStart = useCallback(() => setSsoMessage(null), [])

  const [phase, setPhase] = useState<'validating' | 'ready' | 'accepted' | 'failed'>(
    token === '' ? 'failed' : 'validating'
  )
  const [failedKind, setFailedKind] = useState<FailedKind>('other')
  const [invite, setInvite] = useState<ApiInviteValidation | null>(null)
  const [errorKey, setErrorKey] = useState<string | null>(token === '' ? 'invites:errors.missingToken' : null)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [validation, setValidation] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  // The server refused a signed-in accept for another address (403 email-mismatch).
  const [serverMismatch, setServerMismatch] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const confirmRef = useRef<HTMLInputElement>(null)
  const landingTimer = useRef<number | null>(null)

  useEffect(() => {
    if (token === '') return
    let cancelled = false
    invitesHttp
      .validate(token)
      .then((row) => {
        if (cancelled) return
        setInvite(row)
        setPhase('ready')
      })
      .catch((rejected: unknown) => {
        if (cancelled) return
        setFailedKind(failedKindFor(isApiError(rejected) ? rejected.backendKey : undefined))
        setErrorKey('invites:errors.invalid')
        setPhase('failed')
      })
    return () => {
      cancelled = true
    }
  }, [token])

  // V1 (fix wave 1): the e-mailed link opens a NEW tab, where no guard has
  // restored the session. Probe it without the session-expired bounce, so a
  // signed-in invitee reaches the signed-in or other-account state and a
  // signed-out one simply stays here. Exactly-once by the thunk's own guard.
  useEffect(() => {
    if (token === '') return
    void dispatch(probeSession())
  }, [dispatch, token])

  useEffect(
    () => () => {
      if (landingTimer.current !== null) window.clearTimeout(landingTimer.current)
    },
    []
  )

  const { orgId, projectId, projectName } = invite ?? {}
  const landing = useMemo<InviteLanding>(
    () => ({
      ...(present(orgId) ? { orgId } : {}),
      ...(present(projectId) ? { projectId } : {})
    }),
    [orgId, projectId]
  )
  const landingPath = inviteLandingPath(landing)

  /** Refresh the session (the person just joined an org), select it, then go. */
  const enterLanding = useCallback(async (): Promise<void> => {
    try {
      await dispatch(refreshMe()).unwrap()
      if (present(orgId)) dispatch(activeOrgSelected(orgId))
    } catch {
      // A failed refresh still lands: the guards send an unauthenticated visitor to /login.
    }
    landingTimer.current = window.setTimeout(() => navigate(landingPath), LANDING_DELAY_MS)
  }, [dispatch, navigate, orgId, landingPath])

  const inviteEmail = invite?.email ?? ''
  const emailsMatch = inviteEmail === '' || sessionEmail.toLowerCase() === inviteEmail.toLowerCase()
  const mode: 'public' | 'signedIn' | 'otherAccount' = !signedIn
    ? 'public'
    : serverMismatch || !emailsMatch
      ? 'otherAccount'
      : 'signedIn'

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (phase !== 'ready') return
      const isNewUser = invite?.isExistingUser !== true && mode === 'public'
      const errors: Record<string, string> = {}
      if (isNewUser) {
        if (name.trim() === '') errors['name'] = 'invites:errors.validation.nameRequired'
        else if (name.trim().length < 2) errors['name'] = 'invites:errors.validation.nameTooShort'
        if (password.length === 0) errors['password'] = 'invites:errors.validation.passwordRequired'
        else if (confirm.length === 0) errors['confirm'] = 'invites:errors.validation.confirmRequired'
        else if (confirm !== password) errors['confirm'] = 'invites:errors.validation.passwordMismatch'
      }
      setValidation(errors)
      setSsoMessage(null)
      if (Object.keys(errors).length > 0) {
        const first = errors['name'] ? nameRef : errors['password'] ? passwordRef : confirmRef
        first.current?.focus()
        return
      }

      setSubmitting(true)
      try {
        const entered =
          mode === 'signedIn'
            ? await invitesHttp.acceptSignedInAndEnter(token)
            : await invitesHttp.acceptAndEnter({
                token,
                ...(isNewUser ? { name: name.trim(), password } : {})
              })
        if (!entered) throw new Error('no access token')
        // The password has done its job; drop it before anything renders again.
        setPassword('')
        setConfirm('')
        setPhase('accepted')
        void enterLanding()
      } catch (rejected) {
        setErrorKey(null)
        const backendKey =
          typeof rejected === 'object' && rejected !== null && 'backendKey' in rejected
            ? String((rejected as { backendKey?: string }).backendKey ?? '')
            : 'generic'
        if (backendKey === ACCOUNT_EXISTS) {
          // Not a form error: the account exists, so the way forward is signing in.
          setSsoMessage({ key: ACCOUNT_EXISTS, fromStart: true })
        } else if (backendKey === EMAIL_MISMATCH) {
          setServerMismatch(true)
        } else {
          setValidation({ form: backendKey })
        }
      } finally {
        setSubmitting(false)
      }
    },
    [phase, invite, mode, name, password, confirm, token, enterLanding]
  )

  const onSignOutClicked = useCallback(async (): Promise<void> => {
    setSigningOut(true)
    await dispatch(signOut())
    setSigningOut(false)
    setServerMismatch(false)
  }, [dispatch])

  /** Sign out, then carry an existing account to /login (the stash brings it back here). */
  const onSignOutAndContinueClicked = useCallback(async (): Promise<void> => {
    await onSignOutClicked()
    if (invite?.isExistingUser === true) {
      ssoInviteStash.put(token, landing)
      navigate('/login')
    }
    // A new invitee has no account to sign in with: the page re-renders as ready.
  }, [onSignOutClicked, invite, token, navigate, landing])

  const isNewUser = invite?.isExistingUser !== true
  const roleLabel =
    invite?.role === undefined || invite.role === ''
      ? ''
      : t(`peopleAdmin:roles.${invite.role.toLowerCase()}`, { defaultValue: invite.role })

  /** A field's translated validation message, when it has one. */
  const fieldMessage = (field: string): string | undefined => {
    const key = validation[field]
    return key === undefined ? undefined : t(key)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card px-6 py-6">
        <div className="flex items-center justify-between">
          <Link
            to="/login"
            className="flex h-8 w-8 items-center justify-center rounded-md bg-primary font-serif text-sm font-bold text-primary-foreground"
            aria-label="PX home"
          >
            PX
          </Link>
        </div>

        {(phase === 'validating' || (phase === 'ready' && sessionSettling)) && (
          <p role="status" className="mt-6 text-sm text-muted-foreground">
            {t('validating')}
          </p>
        )}

        {phase === 'failed' && (
          <div className="mt-6" role="alert">
            {failedKind === 'expired' ? (
              <Clock className="h-6 w-6 text-review" aria-hidden />
            ) : failedKind === 'accepted' ? (
              <CircleCheck className="h-6 w-6 text-done" aria-hidden />
            ) : (
              <CircleAlert className="h-6 w-6 text-blocked" aria-hidden />
            )}
            <h1 className="mt-2 font-serif text-2xl font-semibold">
              {failedKind === 'expired'
                ? t('errors.expiredTitle')
                : failedKind === 'revoked'
                  ? t('errors.revokedTitle')
                  : failedKind === 'accepted'
                    ? t('errors.acceptedTitle')
                    : t('errors.invalidTitle')}
            </h1>
            <p
              className="mt-2 text-sm text-muted-foreground"
              data-testid="invite-failed-message"
              data-message-key={
                ssoMessage?.key === 'oauth.invite.invalid'
                  ? 'invites:sso.errors.invalid'
                  : failedKind !== 'other'
                    ? `invites:errors.${failedKind}`
                    : (errorKey ?? 'invites:errors.invalid')
              }
            >
              {ssoMessage?.key === 'oauth.invite.invalid'
                ? t('sso.errors.invalid')
                : failedKind !== 'other'
                  ? t(`errors.${failedKind}`)
                  : errorKey !== null
                    ? t(errorKey)
                    : t('errors.invalid')}
            </p>
            <Link
              to="/login"
              className={cn(
                'mt-4 inline-flex h-9 items-center justify-center rounded-md px-4 text-sm max-sm:min-h-[44px]',
                failedKind === 'accepted'
                  ? 'bg-primary font-medium text-primary-foreground hover:bg-primary/90'
                  : 'border border-input text-ink-soft hover:bg-muted'
              )}
            >
              {t('common:actions.signIn', { ns: 'common' })}
            </Link>
          </div>
        )}

        {phase === 'accepted' && (
          <div className="mt-6" role="status">
            <CircleCheck className="h-6 w-6 text-done" aria-hidden />
            <h1 className="mt-2 font-serif text-2xl font-semibold">
              {present(projectName)
                ? t('accepted.titleProject', { project: projectName })
                : t('accepted.title')}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {present(projectName)
                ? t('accepted.bodyProject')
                : t('accepted.bodyOrg', { org: invite?.orgName ?? '' })}
            </p>
            <Link
              to={landingPath}
              data-testid="invite-go-now"
              className="mt-3 inline-block text-sm font-medium text-foreground underline underline-offset-4 max-sm:py-3"
            >
              {t('accepted.goNow')}
            </Link>
          </div>
        )}

        {phase === 'ready' && !sessionSettling && invite !== null && (
          <div className="mt-6">
            <h1 className="font-serif text-2xl font-semibold">
              {mode === 'otherAccount' ? t('otherAccount.title') : t('title')}
            </h1>
            {mode !== 'otherAccount' && (
              <p className="mt-1 text-sm text-muted-foreground">
                {present(projectName) ? t('ledeProject') : t('lede')}
              </p>
            )}

            {mode === 'otherAccount' && (
              <div data-testid="invite-other-account" className="mt-3">
                <p className="min-w-0 break-words text-sm text-muted-foreground">
                  {t('otherAccount.body', { current: sessionEmail, invited: inviteEmail })}
                </p>
                <div className="mt-5 grid gap-2">
                  <button
                    type="button"
                    data-testid="invite-sign-out-continue"
                    onClick={() => void onSignOutAndContinueClicked()}
                    className={primaryButtonClass}
                  >
                    {t('otherAccount.signOutContinue')}
                  </button>
                  <Link to="/" data-testid="invite-stay-signed-in" className={secondaryButtonClass}>
                    <span className="min-w-0 break-words">{t('otherAccount.stay', { current: sessionEmail })}</span>
                  </Link>
                </div>
              </div>
            )}

            {mode !== 'otherAccount' && ssoMessage !== null && (
              <div className="mt-4">
                <SsoAlert
                  tone={ssoErrorTone(ssoMessage.key)}
                  autoFocus={ssoMessage.fromStart}
                  messageKey={inviteSsoMessage(ssoMessage.key)}
                >
                  {t(inviteSsoMessage(ssoMessage.key), { email: invite.email ?? '' })}
                  {ssoMessage.key === ACCOUNT_EXISTS && (
                    <>
                      {' '}
                      <Link
                        to="/login"
                        data-testid="sign-in-to-accept"
                        // The token rides the tab's stash, never a URL: /login
                        // returns here after a password sign-in.
                        onClick={() => ssoInviteStash.put(token, landing)}
                        className="font-medium text-foreground underline underline-offset-4"
                      >
                        {t('invites:signInToAccept.link')}
                      </Link>
                    </>
                  )}
                </SsoAlert>
              </div>
            )}

            {mode !== 'otherAccount' && (
              <dl className="mt-4 space-y-1.5 text-sm">
                {mode === 'public' && present(invite.email) && (
                  <InviteFact label={t('invitedAs')}>{invite.email}</InviteFact>
                )}
                {present(invite.orgName) && <InviteFact label={t('joining')}>{invite.orgName}</InviteFact>}
                {present(projectName) && <InviteFact label={t('project')}>{projectName}</InviteFact>}
                {present(projectName) && typeof invite.projectAccessLevel === 'string' && (
                  <InviteFact label={t('access')}>
                    {t(`projectWorkspace:members.access.${invite.projectAccessLevel}`)}
                    <small className="block text-xs font-normal text-muted-foreground">
                      {t(`accessHint.${invite.projectAccessLevel}`)}
                    </small>
                  </InviteFact>
                )}
                {roleLabel !== '' && <InviteFact label={t('role')}>{roleLabel}</InviteFact>}
                {present(invite.inviterName) && (
                  <InviteFact label={t('invitedBy')}>{invite.inviterName}</InviteFact>
                )}
              </dl>
            )}

            {mode !== 'otherAccount' && (
              <form className="mt-5 space-y-3" noValidate onSubmit={(event) => void onSubmit(event)}>
                {mode === 'signedIn' ? (
                  <p data-testid="invite-signed-in-as" className="min-w-0 break-words text-sm text-ink-soft">
                    {t('signedInAs', { email: sessionEmail })}
                  </p>
                ) : (
                  <p className="text-sm text-ink-soft">{isNewUser ? t('newIntro') : t('existingIntro')}</p>
                )}

                {mode === 'public' && isNewUser && (
                  <>
                    <InviteField
                      field="name"
                      label={t('name')}
                      inputRef={nameRef}
                      type="text"
                      autoComplete="name"
                      value={name}
                      onValueChanged={setName}
                      error={fieldMessage('name')}
                    />
                    <InviteField
                      field="password"
                      label={t('password')}
                      inputRef={passwordRef}
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onValueChanged={setPassword}
                      error={fieldMessage('password')}
                    />
                    <InviteField
                      field="confirm"
                      label={t('confirmPassword')}
                      inputRef={confirmRef}
                      type="password"
                      autoComplete="new-password"
                      value={confirm}
                      onValueChanged={setConfirm}
                      error={fieldMessage('confirm')}
                    />
                  </>
                )}

                {validation['form'] !== undefined && (
                  <p role="alert" className="text-sm text-blocked">
                    {toMessage({ status: 400, backendKey: validation['form'] ?? undefined, message: '' }) ??
                      t('errors.invalid')}
                  </p>
                )}

                <button type="submit" disabled={submitting} className={primaryButtonClass}>
                  {submitting ? t('accepting') : t('accept')}
                </button>

                {mode === 'signedIn' && (
                  <p className="text-sm text-muted-foreground">
                    {t('notYou')}{' '}
                    <button
                      type="button"
                      data-testid="invite-sign-out"
                      onClick={() => void onSignOutClicked()}
                      className="font-medium text-foreground underline underline-offset-4 max-sm:min-h-[44px]"
                    >
                      {t('common:actions.signOut')}
                    </button>
                  </p>
                )}
              </form>
            )}

            {/* After </form>: Enter in a field always submits the password form. */}
            {mode === 'public' && (
              <SsoButtons
                mode={{ kind: 'invite', token, landing }}
                onError={onSsoError}
                onStart={onSsoStart}
                hint={
                  <p data-testid="sso-hint" className="flex gap-2 text-xs text-muted-foreground">
                    <Info aria-hidden className="mt-px size-3.5 shrink-0" />
                    <span className="min-w-0 break-words">{t('sso.hint', { email: invite.email ?? '' })}</span>
                  </p>
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** One row of the invitation's facts. */
function InviteFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words font-medium text-foreground">{children}</dd>
    </div>
  )
}

interface InviteFieldProps {
  /** Names the error element (`invite-<field>-error`) the input is described by. */
  field: 'name' | 'password' | 'confirm'
  label: string
  inputRef: RefObject<HTMLInputElement | null>
  type: 'text' | 'password'
  autoComplete: string
  value: string
  onValueChanged: (value: string) => void
  /** The field's translated validation message, when it has one. */
  error: string | undefined
}

/** One labelled field of the new-account form, with its own error line. */
function InviteField({ field, label, inputRef, type, autoComplete, value, onValueChanged, error }: InviteFieldProps) {
  const errorId = `invite-${field}-error`
  return (
    <>
      <label className="flex flex-col gap-1 text-sm text-ink-soft">
        {label}
        <input
          ref={inputRef}
          type={type}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onValueChanged(event.target.value)}
          aria-invalid={error !== undefined}
          aria-describedby={error !== undefined ? errorId : undefined}
          className={cn(inputClass, 'w-full')}
        />
      </label>
      {error !== undefined && (
        <p id={errorId} role="alert" className="text-sm text-blocked">
          {error}
        </p>
      )}
    </>
  )
}
